import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * "Forgot your password?" — the two way-in pages (owner request 2026-10-12), drawn in the public design's frame like
 * the restyled Log in and Sign up (ADR 0056's portal amendment): `PublicFrame`, its tokens on `.pd-page`, one card.
 *
 *   /forgot-password          the address → "If an account exists for it, we've sent a link" — the SAME page for every
 *                             address, because the API gives the same answer for every address (no enumeration).
 *   /reset-password#<token>   the token from the emailed link's FRAGMENT (never a query string: a fragment reaches no
 *                             server log, proxy or Referer), read once and taken out of the address bar; a new password
 *                             and its confirmation with the policy's hint; success → Log in. Nobody is signed in here:
 *                             the API signs every device out, and the person's next sign-in still asks for their
 *                             authenticator if they have one.
 *
 * Opening the reset link changes nothing: the token is spent only by the form's submit, so a mail scanner or a link
 * preview that fetches the page cannot use it. Every unusable link (used, expired, replaced, never issued) is one page,
 * as the API gives one answer for all of them. Focus moves to each new state's heading, so a screen-reader user hears
 * where they are; every refusal is announced (`role="alert"` on the field's error).
 */
import { useMutation } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { Field, Icon, Input } from "@wetalk/ui";
import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState, type FormEvent, type ReactNode, type RefObject } from "react";

import { apiProblemText } from "../../api/api-error.js";
import { useGateway } from "../../api/provider.js";
import { GLYPH } from "../../shell/glyph.js";
import { ArrowMark, EnvelopeMark, PublicFrame, PublicKicker } from "../auth/public-frame.js";
import "../auth/auth.css";
import "./password-reset-gateway.js";

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** The API's policy (`PASSWORD_MIN_LENGTH` / `PASSWORD_MAX_LENGTH`), restated: apps never import apps. */
const PASSWORD_MINIMUM = 10;
const PASSWORD_MAXIMUM = 1024;

/** Moves focus to the card's heading whenever `step` changes (never on the first paint). */
function useHeadingFocus(step: string): RefObject<HTMLDivElement | null> {
  const card = useRef<HTMLDivElement | null>(null);
  const previous = useRef(step);
  useEffect(() => {
    if (previous.current === step) return;
    previous.current = step;
    card.current?.querySelector<HTMLElement>("h1")?.focus();
  }, [step]);
  return card;
}

/** "Remembered it? Log in" — under every form, above the card's rule (the log-in page's `pd-switch`). */
function BackToLogIn(props: { readonly lead: string }): ReactNode {
  usePortalTranslation("core");
  return (
    <p className="pd-switch">
      {props.lead}{" "}
      <Link to="/log-in">{portalText("core", "auth.log_in_f7c400")}</Link>
    </p>
  );
}

/** A notice inside the card: an icon and a sentence (the log-in page's SSO notice block). */
function CardNotice(props: { readonly glyph: (typeof GLYPH)[keyof typeof GLYPH]; readonly children: ReactNode }): ReactNode {
  return (
    <div className="c-entry-governed" role="status">
      <Icon glyph={props.glyph} size="lg" tone="accent" />
      <span>{props.children}</span>
    </div>
  );
}

/** Shown when the gateway cannot reset a password at all (or the API says no email can be sent). */
function Unavailable(): ReactNode {
  usePortalTranslation("core");
  return (
    <PublicFrame>
      <PublicKicker>{portalText("core", "password_reset.kicker")}</PublicKicker>
      <h1 tabIndex={-1}>{portalText("core", "password_reset.unavailable_title")}</h1>
      <p className="pd-lede">{portalText("core", "password_reset.unavailable_lede")}</p>
      <BackToLogIn lead={portalText("core", "password_reset.back_lead")} />
    </PublicFrame>
  );
}

const isNotFound = (raised: unknown): boolean => raised instanceof ApiError && raised.code === "not_found";

// ── /forgot-password ──────────────────────────────────────────────────────────────────────────────────────────

export function ForgotPasswordScreen(): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const ask = gateway.requestPasswordReset;
  const [emailAddress, setEmailAddress] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const card = useHeadingFocus(sentTo === null ? "ask" : "sent");

  const send = useMutation({
    mutationFn: (address: string) => {
      if (ask === undefined) throw new Error("unavailable");
      return ask(address);
    },
    onSuccess: (_answer, address) => {
      setSentTo(address);
    },
    onError: (raised) => {
      setProblem(apiProblemText(raised, portalText("core", "password_reset.could_not_send")));
    },
  });

  if (ask === undefined || (send.isError && isNotFound(send.error))) return <Unavailable />;

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    setProblem(null);
    const address = emailAddress.trim();
    if (!EMAIL_SHAPE.test(address)) {
      setProblem(portalText("core", "auth.that_does_not_look_like_an_email_address_8b7893"));
      return;
    }
    send.mutate(address);
  };

  if (sentTo !== null) {
    return (
      <PublicFrame>
        <div ref={card} className="pd-flow">
          <PublicKicker>{portalText("core", "password_reset.kicker")}</PublicKicker>
          <h1 tabIndex={-1}>{portalText("core", "auth.entry.check_your_email")}</h1>
          <CardNotice glyph={GLYPH.envelope}>{portalText("core", "password_reset.sent", { email: sentTo })}</CardNotice>
          <p className="c-entry-code-note">{portalText("core", "password_reset.sent_note")}</p>
          <p className="c-entry-code-note">{portalText("core", "password_reset.sent_sso_note")}</p>
          <button
            type="button"
            className="pd-cta"
            onClick={() => {
              send.reset();
              setSentTo(null);
            }}
          >
            {portalText("core", "password_reset.send_again")}
          </button>
          <BackToLogIn lead={portalText("core", "password_reset.back_lead")} />
        </div>
      </PublicFrame>
    );
  }

  return (
    <PublicFrame>
      <div ref={card} className="pd-flow">
        <PublicKicker>{portalText("core", "password_reset.kicker")}</PublicKicker>
        <h1 tabIndex={-1}>{portalText("core", "password_reset.forgot_title")}</h1>
        <p className="pd-lede">{portalText("core", "password_reset.forgot_lede")}</p>
        <form onSubmit={onSubmit} noValidate aria-label={portalText("core", "password_reset.forgot_title")}>
          <Field label={portalText("core", "auth.entry.work_email")} {...(problem === null ? {} : { error: problem })}>
            {(control) => (
              <div className="pd-input-icon">
                <EnvelopeMark />
                <Input
                  {...control}
                  name="emailAddress"
                  type="email"
                  value={emailAddress}
                  placeholder={portalText("core", "auth.you_business_com_48ac25")}
                  autoComplete="username"
                  onChange={(event) => {
                    setEmailAddress(event.currentTarget.value);
                  }}
                />
              </div>
            )}
          </Field>
          <button type="submit" className="pd-cta" disabled={send.isPending}>
            {send.isPending ? portalText("core", "auth.sending_cf7655") : portalText("core", "password_reset.send_link")}
            <ArrowMark />
          </button>
        </form>
        <BackToLogIn lead={portalText("core", "password_reset.back_lead")} />
      </div>
    </PublicFrame>
  );
}

// ── /reset-password#<token> ───────────────────────────────────────────────────────────────────────────────────

export interface ResetPasswordScreenProps {
  /** The token from the link's fragment, exactly as it arrived (never logged); empty for none. */
  readonly token: string;
}

/** Every unusable link — used, expired, replaced, incomplete — is this one page. */
function Unusable(): ReactNode {
  usePortalTranslation("core");
  return (
    <PublicFrame>
      <PublicKicker>{portalText("core", "password_reset.kicker")}</PublicKicker>
      <h1 tabIndex={-1}>{portalText("core", "mail_landing.this_link_cannot_be_used_fcb489")}</h1>
      <p className="pd-lede">{portalText("core", "password_reset.unusable_lede")}</p>
      <Link to="/forgot-password" className="pd-cta">
        {portalText("core", "password_reset.ask_again")}
        <ArrowMark />
      </Link>
      <BackToLogIn lead={portalText("core", "password_reset.back_lead")} />
    </PublicFrame>
  );
}

export function ResetPasswordScreen(props: ResetPasswordScreenProps): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const confirmIt = gateway.confirmPasswordReset;
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [passwordProblem, setPasswordProblem] = useState<string | null>(null);
  const [againProblem, setAgainProblem] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (newPassword: string) => {
      if (confirmIt === undefined) throw new Error("unavailable");
      return confirmIt(props.token, newPassword);
    },
    onError: (raised) => {
      const text = apiProblemText(raised, portalText("core", "password_reset.could_not_save"));
      // A refused password belongs to its field; anything else (a busy network, a fault) to the form.
      if (raised instanceof ApiError && raised.field === "new_password") setPasswordProblem(text);
      else setProblem(text);
    },
  });
  const card = useHeadingFocus(save.isSuccess ? "done" : "form");

  if (confirmIt === undefined) return <Unavailable />;
  if (props.token === "" || (save.isError && isNotFound(save.error))) return <Unusable />;

  if (save.isSuccess) {
    return (
      <PublicFrame>
        <div ref={card} className="pd-flow">
          <PublicKicker>{portalText("core", "password_reset.kicker")}</PublicKicker>
          <h1 tabIndex={-1}>{portalText("core", "password_reset.done_title")}</h1>
          <CardNotice glyph={GLYPH.checkCircle}>{portalText("core", "password_reset.done")}</CardNotice>
          <Link to="/log-in" className="pd-cta">
            {portalText("core", "auth.log_in_f7c400")}
            <ArrowMark />
          </Link>
        </div>
      </PublicFrame>
    );
  }

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    setProblem(null);
    const first =
      password.length < PASSWORD_MINIMUM
        ? portalText("core", "password_reset.too_short")
        : password.length > PASSWORD_MAXIMUM
          ? portalText("core", "password_reset.too_long")
          : null;
    const second = first === null && again !== password ? portalText("core", "password_reset.mismatch") : null;
    setPasswordProblem(first);
    setAgainProblem(second);
    if (first !== null || second !== null) return;
    save.mutate(password);
  };

  return (
    <PublicFrame>
      <div ref={card} className="pd-flow">
        <PublicKicker>{portalText("core", "password_reset.kicker")}</PublicKicker>
        <h1 tabIndex={-1}>{portalText("core", "password_reset.reset_title")}</h1>
        <p className="pd-lede">{portalText("core", "password_reset.reset_lede")}</p>
        <form onSubmit={onSubmit} noValidate aria-label={portalText("core", "password_reset.reset_title")}>
          <Field
            label={portalText("core", "password_reset.new_password")}
            description={portalText("core", "auth.at_least_10_characters_37e136")}
            {...(passwordProblem === null ? {} : { error: passwordProblem })}
          >
            {(control) => (
              <Input
                {...control}
                name="newPassword"
                type="password"
                value={password}
                autoComplete="new-password"
                onChange={(event) => {
                  setPassword(event.currentTarget.value);
                }}
              />
            )}
          </Field>
          <Field label={portalText("core", "password_reset.confirm_password")} {...(againProblem === null ? {} : { error: againProblem })}>
            {(control) => (
              <Input
                {...control}
                name="confirmNewPassword"
                type="password"
                value={again}
                autoComplete="new-password"
                onChange={(event) => {
                  setAgain(event.currentTarget.value);
                }}
              />
            )}
          </Field>
          {problem === null ? null : (
            <p className="c-entry-code-note" role="alert">
              {problem}
            </p>
          )}
          <button type="submit" className="pd-cta" disabled={save.isPending}>
            {save.isPending ? portalText("core", "password_reset.saving") : portalText("core", "password_reset.save")}
            <ArrowMark />
          </button>
        </form>
        <p className="c-entry-code-note">{portalText("core", "password_reset.signs_out_note")}</p>
        <BackToLogIn lead={portalText("core", "password_reset.back_lead")} />
      </div>
    </PublicFrame>
  );
}
